<template>
  <div class="map-container">
    <div ref="mapContainer" class="map"></div>
  </div>
</template>
<script>
import { onMounted, ref, nextTick } from 'vue';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
// import length from '@turf/length';
// import along from '@turf/along';
// import { lineString } from '@turf/helpers';
import pinRouteGeojson from '../assets/zuk2022.json';

export default {
  name: 'RouteView',
  setup() {
    const ZOOM = 12;
    const mapContainer = ref(null);
    let map;

    onMounted(async () => {
      await nextTick();
      // Process route data
      const allCoordinates = [];
      pinRouteGeojson.features.forEach(feature => {
        if (feature.geometry.type === 'LineString') {
          allCoordinates.push(...feature.geometry.coordinates);
        }
      });
      const pinRoute = allCoordinates;

      if (!mapContainer.value) {
        console.error('Map container not found');
        return;
      }

      mapboxgl.config.MAX_PARALLEL_IMAGE_REQUESTS = 12;

      mapboxgl.accessToken = 'pk.eyJ1IjoiamdhbGxhIiwiYSI6ImNrcDQ5dnM0YzBjOXUycGxyb2o2eHFmcHkifQ.rE5hlqylvXmFmvLS3jp1sw'; // Replace with your Mapbox access token

      map = (window.map = new mapboxgl.Map({
        container: mapContainer.value,
        zoom: ZOOM-2,
        center: [20.3150203, 49.4357597],
        pitch: 60,
        bearing: 150,
        style: 'mapbox://styles/mapbox/standard-satellite',
        interactive: false,
        hash: false,
        maxTileCacheSize: 150 * 1024 * 1024 // 150MB
      }));

      // Wait for map style to load
      await map.once('style.load');

      // Preload tiles for the route area to improve animation performance
      const routeBounds = pinRoute.reduce((b, coord) => {
        return b.extend(coord);
      }, new mapboxgl.LngLatBounds(pinRoute[0], pinRoute[0]));

      // Temporarily fit bounds to preload tiles (will be visible briefly)
      map.fitBounds(routeBounds, { duration: 100 });
      await new Promise(resolve => setTimeout(resolve, 2000)); // Wait for tiles to load

      // Reset to initial view
      const startPoint = pinRoute[0];

      map.flyTo({
        center: startPoint,
        zoom: ZOOM,
        bearing: 0,
        pitch: 60,
        duration: 1000
      });

      // Set custom fog
      map.setFog({
        'range': [-0.1, 5],
        'color': '#def',
        'high-color': '#def',
        'space-color': '#def'
      });

      // Add terrain source, with slight exaggeration (optimized for performance)
      map.addSource('mapbox-dem', {
        'type': 'raster-dem',
        'url': 'mapbox://mapbox.terrain-rgb',
        'tileSize': 512,
        'maxzoom': ZOOM // Reduced from 14 for better performance
      });
      map.setTerrain({ 'source': 'mapbox-dem', 'exaggeration': 1.5 }); // Reduced exaggeration

       map.addSource('line', {
        type: 'geojson',
        // Line metrics is required to use the 'line-progress' property
        lineMetrics: true,
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: pinRoute
          }
        }
      });
      map.addLayer({
        type: 'line',
        source: 'line',
        id: 'line',
        paint: {
          'line-color': 'rgba(52,152,219,0.8)',
          'line-width': 9
        },
        layout: {
          'line-cap': 'round',
          'line-join': 'round'
        }
      });
      await map.once('idle');
      
      // calculate simplified route for camera path
      const cameraPath = pinRoute.filter((_, index) => index % 500 === 0);
      // add 1 extra point in the end
        cameraPath.push(pinRoute[pinRoute.length - 1]);
        // add 9 9 extra points between each to smooth the path
        const detailedCameraPath = [];
        for (let i = 0; i < cameraPath.length - 1; i++) {
          const start = cameraPath[i];
          const end = cameraPath[i + 1];
          detailedCameraPath.push(start);
          for (let j = 1; j < 100; j++) {
            const interpolated = [
              start[0] + (end[0] - start[0]) * (j / 100),
              start[1] + (end[1] - start[1]) * (j / 100)
            ];
            detailedCameraPath.push(interpolated);
          }
        }
    //  add 1000 points at the end between last point and start point to close the loop smoothly
      const lastPoint = cameraPath[cameraPath.length - 1];
        for (let j = 1; j < 1000; j++) {
            const interpolated = [
            lastPoint[0] + (startPoint[0] - lastPoint[0]) * (j / 1000),
            lastPoint[1] + (startPoint[1] - lastPoint[1]) * (j / 1000)
            ];
            detailedCameraPath.push(interpolated);
        }
      const paused = ref(false);
      let currentCenter = { lng: startPoint[0], lat: startPoint[1] };
      let targetCenter = { lng: detailedCameraPath[0][0], lat: detailedCameraPath[0][1] };
      let cameraIndex = 0;

      // Linear interpolation function for smooth camera movement
      function lerp(start, end, amt) {
        return (1 - amt) * start + amt * end;
      }

      // Frame rate throttling for performance
      let lastFrameTime = 0;
      const targetFPS = 30;
      const frameInterval = 1000 / targetFPS;

      function frame(time) {
        if (time - lastFrameTime < frameInterval) {
          window.requestAnimationFrame(frame);
          return;
        }
        lastFrameTime = time;

        if (!paused.value) {
          // Smooth camera movement towards the target
          currentCenter.lng = lerp(currentCenter.lng, targetCenter.lng, 0.95);
          currentCenter.lat = lerp(currentCenter.lat, targetCenter.lat, 0.95);
          map.setCenter(currentCenter, { duration: frameInterval });

          // Check if close to target, move to next
          const dist = Math.sqrt((currentCenter.lng - targetCenter.lng)**2 + (currentCenter.lat - targetCenter.lat)**2);
          if (dist < 0.01) { // close enough
            cameraIndex = (cameraIndex + 1) % detailedCameraPath.length;
            targetCenter = { lng: detailedCameraPath[cameraIndex][0], lat: detailedCameraPath[cameraIndex][1] };
          }

          // Continuous bearing rotation
          const bearing = (time * 0.003) % 360;
          map.setBearing(bearing);
        }

        window.requestAnimationFrame(frame);
      }

      window.requestAnimationFrame(frame);
    });

    return {
      mapContainer
    };
  }
};
</script>

<style scoped>
.map-container {
  height: 100vh;
  width: 100%;
  position: relative;
}

.animation-controls {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1;
}

.animation-controls button {
  margin-right: 10px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
}

.animation-controls button.active {
  background: rgba(0, 123, 255, 0.8);
  color: white;
}

.map {
  height: 100%;
  width: 100%;
}
</style>